// Comprehension Question Component
import React from "react";
import { QuestionBuilderState } from "../../../../types/question";

interface ComprehensionProps {
  question: QuestionBuilderState;
  updateQuestion: (field: keyof QuestionBuilderState, value: any) => void;
  updateTypeSpecificData: (field: string, value: any) => void;
  errors: Record<string, string>;
  clearError?: (field: string) => void;
}

const Comprehension: React.FC<ComprehensionProps> = ({
  question,
  updateQuestion,
  updateTypeSpecificData,
  errors,
  clearError,
}) => {
  const data = question.typeSpecificData;

  const updateSubQuestion = (index: number, field: string, value: any) => {
    const currentSubQuestions = data.subQuestions || [];
    const newSubQuestions = [...currentSubQuestions];
    newSubQuestions[index] = { ...newSubQuestions[index], [field]: value };
    updateTypeSpecificData("subQuestions", newSubQuestions);

    // Clear related error
    if (clearError) {
      const errorKey = `subQuestion_${index}_${field}`;
      clearError(errorKey);
    }
  };

  const addSubQuestion = () => {
    const currentSubQuestions = data.subQuestions || [];
    const newSubQuestions = [
      ...currentSubQuestions,
      {
        _id: `temp_${Date.now()}_${currentSubQuestions.length + 1}`,
        type: "multiple_choice",
        question: "",
        options: ["", "", "", ""],
        correctAnswer: "",
        points: 1,
      },
    ];
    updateTypeSpecificData("subQuestions", newSubQuestions);
  };

  const removeSubQuestion = (index: number) => {
    const currentSubQuestions = data.subQuestions || [];
    if (currentSubQuestions.length > 1) {
      const newSubQuestions = currentSubQuestions.filter(
        (_: any, i: number) => i !== index
      );
      updateTypeSpecificData("subQuestions", newSubQuestions);
    }
  };

  const renderSubQuestion = (subQ: any, index: number) => {
    return (
      <div
        key={subQ._id || index}
        className="border border-gray-200 rounded-lg p-4 mb-4"
      >
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-medium text-gray-700">
            Question {index + 1}
          </h4>
          {data.subQuestions.length > 1 && (
            <button
              type="button"
              onClick={() => removeSubQuestion(index)}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Text *
            </label>
            <input
              type="text"
              value={subQ.question || ""}
              onChange={(e) =>
                updateSubQuestion(index, "question", e.target.value)
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors[`subQuestion_${index}_question`]
                  ? "border-red-300"
                  : "border-gray-300"
              }`}
              placeholder="Enter the question..."
            />
            {errors[`subQuestion_${index}_question`] && (
              <p className="mt-1 text-sm text-red-600">
                {errors[`subQuestion_${index}_question`]}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={subQ.type || "multiple_choice"}
                onChange={(e) =>
                  updateSubQuestion(index, "type", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">True/False</option>
                <option value="short_answer">Short Answer</option>
                <option value="essay">Essay</option>
                <option value="fill_blank">Fill in the Blank</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points
              </label>
              <input
                type="number"
                value={subQ.points || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = value === "" ? "" : parseInt(value) || 1;
                  updateSubQuestion(index, "points", numValue);
                }}
                min="1"
                placeholder="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors[`subQuestion_${index}_points`]
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
              />
              {errors[`subQuestion_${index}_points`] && (
                <p className="mt-1 text-sm text-red-600">
                  {errors[`subQuestion_${index}_points`]}
                </p>
              )}
            </div>
          </div>

          {subQ.type === "multiple_choice" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Options
              </label>
              <div className="space-y-2">
                {(subQ.options || ["", "", "", ""]).map(
                  (option: string, optIndex: number) => (
                    <div key={optIndex} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name={`correct-${index}`}
                        checked={subQ.correctAnswer === option}
                        onChange={() =>
                          updateSubQuestion(index, "correctAnswer", option)
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [
                            ...(subQ.options || ["", "", "", ""]),
                          ];
                          newOptions[optIndex] = e.target.value;
                          updateSubQuestion(index, "options", newOptions);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Option ${optIndex + 1}`}
                      />
                    </div>
                  )
                )}
              </div>
              {errors[`subQuestion_${index}_correctAnswer`] && (
                <p className="mt-1 text-sm text-red-600">
                  {errors[`subQuestion_${index}_correctAnswer`]}
                </p>
              )}
            </div>
          )}

          {subQ.type === "true_false" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correct Answer
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`tf-${index}`}
                    value="True"
                    checked={subQ.correctAnswer === "True"}
                    onChange={(e) =>
                      updateSubQuestion(index, "correctAnswer", e.target.value)
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span>True</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`tf-${index}`}
                    value="False"
                    checked={subQ.correctAnswer === "False"}
                    onChange={(e) =>
                      updateSubQuestion(index, "correctAnswer", e.target.value)
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span>False</span>
                </label>
              </div>
              {errors[`subQuestion_${index}_correctAnswer`] && (
                <p className="mt-1 text-sm text-red-600">
                  {errors[`subQuestion_${index}_correctAnswer`]}
                </p>
              )}
            </div>
          )}

          {(subQ.type === "short_answer" || subQ.type === "fill_blank") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Answer(s)
              </label>
              <input
                type="text"
                value={subQ.correctAnswers?.[0] || ""}
                onChange={(e) =>
                  updateSubQuestion(index, "correctAnswers", [e.target.value])
                }
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors[`subQuestion_${index}_correctAnswers`]
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="Enter correct answer..."
              />
              {errors[`subQuestion_${index}_correctAnswers`] && (
                <p className="mt-1 text-sm text-red-600">
                  {errors[`subQuestion_${index}_correctAnswers`]}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reading Passage *
        </label>
        <textarea
          value={data.passage || ""}
          onChange={(e) => {
            updateTypeSpecificData("passage", e.target.value);
            if (clearError) {
              clearError("passage");
            }
          }}
          rows={8}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.passage ? "border-red-300" : "border-gray-300"
          }`}
          placeholder="Enter the reading passage that students will read before answering questions..."
        />
        {errors.passage && (
          <p className="mt-1 text-sm text-red-600">{errors.passage}</p>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Questions Based on the Passage
          </label>
          <button
            type="button"
            onClick={addSubQuestion}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            + Add Question
          </button>
        </div>

        <div className="space-y-4">
          {(data.subQuestions || []).map((subQ: any, index: number) =>
            renderSubQuestion(subQ, index)
          )}
        </div>

        {errors.subQuestions && (
          <p className="mt-1 text-sm text-red-600">{errors.subQuestions}</p>
        )}
      </div>
    </div>
  );
};

export default Comprehension;
